import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { walletManager, WALLET_TYPES, getAccounts } from './EthersSetup';
import AddressDisplay, { AddressDisplayLong } from './AddressDisplay';
import AddressSelector from './AddressSelector';
import WalletSelection from './basics/WalletSelection';
import { ethers } from 'ethers';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import GameMakerABI from '../deployedContractsInfo/GameMaker.json';
import DICYABI from '../deployedContractsInfo/DICY.json';

/**
 * WalletDetails Component
 * 
 * Comprehensive wallet information page with transaction analysis
 */
const WalletDetails = () => {
  const navigate = useNavigate();
  const [walletInfo, setWalletInfo] = useState(null);
  const [balance, setBalance] = useState(null);
  const [dicyTokens, setDicyTokens] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // Wallet connection state
  const [connectionError, setConnectionError] = useState('');

  // Transfer state
  const [transferForm, setTransferForm] = useState({
    recipient: '',
    amount: '',
    isTransferring: false,
    transferError: '',
    transferSuccess: false
  });

  useEffect(() => {
    loadWalletData();
  }, []);

  // Real blockchain monitoring: Listen for incoming ETH transfers
  useEffect(() => {
    if (!walletInfo?.address) return;

    let isActive = true;
    const provider = walletManager.getProvider();
    
    if (!provider) {
      console.log('No provider available for incoming transfer monitoring');
      return;
    }

    console.log(`👁️ Starting incoming ETH transfer monitoring for ${walletInfo.address}`);

    // Listen for new blocks and check for incoming transactions
    const handleBlock = async (blockNumber) => {
      if (!isActive) return;
      
      try {
        // Get the block with all transactions
        const block = await provider.getBlockWithTransactions(blockNumber);
        if (!block || !block.transactions) return;

        // Check if any transaction sends ETH to our address
        const incomingTxs = block.transactions.filter(tx => 
          tx.to && 
          tx.to.toLowerCase() === walletInfo.address.toLowerCase() && 
          tx.value && 
          !tx.value.isZero()
        );

        if (incomingTxs.length > 0 && isActive) {
          console.log(`💰 Detected ${incomingTxs.length} incoming ETH transfer(s) in block ${blockNumber}`);
          
          // Refresh balance, DICY tokens, and transactions
          await Promise.all([
            fetchBalance(walletInfo.address),
            fetchDicyTokens(walletInfo.address),
            fetchAllTransactions(walletInfo.address)
          ]);
        }
      } catch (error) {
        console.error('Error monitoring incoming transfers:', error);
      }
    };

    // Start listening to new blocks
    provider.on('block', handleBlock);

    return () => {
      isActive = false;
      provider.off('block', handleBlock);
      console.log(`🛑 Stopped incoming ETH transfer monitoring for ${walletInfo.address}`);
    };
  }, [walletInfo?.address]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      
      // Give wallet manager time to auto-connect (especially after refresh)
      let activeProvider = walletManager.getActiveProvider();
      
      if (!activeProvider) {
        // Wait up to 3 seconds for wallet manager to finish initialization
        const maxWaitTime = 3000; // 3 seconds
        const checkInterval = 100; // Check every 100ms
        const maxChecks = maxWaitTime / checkInterval;
        
        for (let i = 0; i < maxChecks; i++) {
          await new Promise(resolve => setTimeout(resolve, checkInterval));
          activeProvider = walletManager.getActiveProvider();
          if (activeProvider) {
            console.log(`✅ Wallet restored after ${(i + 1) * checkInterval}ms`);
            break;
          }
        }
      }
      
      if (!activeProvider) {
        console.log('⚠️ No wallet found after initialization timeout, staying on page for wallet setup');
        // Don't redirect - let user see wallet connection options
        setWalletInfo(null);
        setLoading(false);
        return;
      }

      const accounts = await getAccounts();
      const address = accounts[0];
      const walletType = activeProvider.getType();
      
      setWalletInfo({
        type: walletType,
        address: address,
        isConnected: !!address
      });

      if (address) {
        await Promise.all([
          fetchBalance(address),
          fetchDicyTokens(address),
          fetchAllTransactions(address)
        ]);
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async (address) => {
    try {
      const provider = walletManager.getProvider();
      if (!provider) return;
      
      const balanceWei = await provider.getBalance(address);
      const balanceEth = ethers.utils.formatEther(balanceWei);
      setBalance(balanceEth);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchDicyTokens = async (address) => {
    try {
      const provider = walletManager.getProvider();
      if (!provider || !contractAddresses.DICY) {
        setDicyTokens(null);
        return;
      }
      
      const dicyContract = new ethers.Contract(
        contractAddresses.DICY,
        DICYABI.abi,
        provider
      );
      
      const tokenBalance = await dicyContract.balanceOf(address);
      const tokenValue = await dicyContract.getInvestorInfo(address);
      
      setDicyTokens({
        tokens: tokenBalance.toString(),
        value: ethers.utils.formatEther(tokenValue.currentValue)
      });
    } catch (error) {
      console.error('Error fetching DICY tokens:', error);
      setDicyTokens(null);
    }
  };

  const fetchAllTransactions = async (address) => {
    try {
      setAnalysisLoading(true);
      const provider = walletManager.getProvider();
      if (!provider) return;

      const currentBlock = await provider.getBlockNumber();
      const allTxs = [];
      
      // Optimized approach for detailed view: larger batches, smarter scanning
      const maxBlocksToCheck = 150; // Reduced from 500 but still comprehensive
      const batchSize = 8; // Larger batches for detailed view
      const targetTxCount = 50;
      
      // Process blocks in parallel batches for much better performance
      for (let startBlock = 0; startBlock < maxBlocksToCheck && allTxs.length < targetTxCount; startBlock += batchSize) {
        const endBlock = Math.min(startBlock + batchSize, maxBlocksToCheck);
        const blockNumbers = [];
        
        for (let i = startBlock; i < endBlock; i++) {
          const blockNumber = currentBlock - i;
          if (blockNumber > 0) blockNumbers.push(blockNumber);
        }
        
        // Step 1: Get block headers in parallel (lightweight)
        const blockPromises = blockNumbers.map(async (blockNum) => {
          try {
            const block = await provider.getBlock(blockNum);
            return { blockNum, hasTransactions: block && block.transactions && block.transactions.length > 0 };
          } catch (error) {
            console.warn(`Error getting block ${blockNum}:`, error.message);
            return { blockNum, hasTransactions: false };
          }
        });
        
        const blockHeaders = await Promise.all(blockPromises);
        const blocksWithTxs = blockHeaders.filter(b => b.hasTransactions);
        
        // Step 2: Fetch full transaction data for blocks with transactions
        if (blocksWithTxs.length > 0) {
          const txPromises = blocksWithTxs.map(async ({ blockNum }) => {
            try {
              const block = await provider.getBlockWithTransactions(blockNum);
              if (block && block.transactions) {
                const relevantTxs = block.transactions.filter(tx => 
                  tx.from?.toLowerCase() === address.toLowerCase() || 
                  tx.to?.toLowerCase() === address.toLowerCase()
                );
                
                // Analyze transactions in parallel for better performance
                const analyzedTxPromises = relevantTxs.map(tx => 
                  analyzeTxForGameMaker(tx, address, provider)
                );
                
                return await Promise.all(analyzedTxPromises);
              }
              return [];
            } catch (error) {
              console.warn(`Error processing block ${blockNum}:`, error.message);
              return [];
            }
          });
          
          const txBatches = await Promise.all(txPromises);
          
          // Flatten and add to results
          for (const txBatch of txBatches) {
            for (const tx of txBatch) {
              if (allTxs.length < targetTxCount) {
                allTxs.push(tx);
              }
            }
          }
        }
        
        // Early termination
        if (allTxs.length >= targetTxCount) break;
        
        // Show progress for long-running detailed analysis
        if (allTxs.length > 0) {
          setTransactions([...allTxs].sort((a, b) => b.blockNumber - a.blockNumber));
        }
      }
      
      allTxs.sort((a, b) => b.blockNumber - a.blockNumber);
      setTransactions(allTxs);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const analyzeTxForGameMaker = async (tx, userAddress, provider) => {
    const baseTx = {
      hash: tx.hash,
      from: tx.from,
      to: tx.to,
      value: ethers.utils.formatEther(tx.value || '0'),
      blockNumber: tx.blockNumber,
      gasUsed: tx.gasLimit ? tx.gasLimit.toString() : 'N/A',
      isIncoming: tx.to?.toLowerCase() === userAddress.toLowerCase(),
      gameRelated: false,
      contractType: 'Unknown',
      action: 'Transfer',
      gameId: null,
      timestamp: null
    };

    try {
      // Get transaction receipt for more details
      const receipt = await provider.getTransactionReceipt(tx.hash);
      if (receipt) {
        baseTx.gasUsed = receipt.gasUsed.toString();
        baseTx.status = receipt.status === 1 ? 'Success' : 'Failed';
      }

      // Check if transaction is to a GameMaker contract
      const contractAddress = tx.to?.toLowerCase();
      
      // Special handling for GameMaker transactions - parse events to get actual game type
      if (contractAddress === contractAddresses.GameMaker?.toLowerCase()) {
        baseTx.gameRelated = true;
        
        if (receipt && receipt.logs) {
          const gameMakerInterface = new ethers.utils.Interface(GameMakerABI.abi);
          
          for (const log of receipt.logs) {
            try {
              if (log.address.toLowerCase() === contractAddress) {
                const parsedLog = gameMakerInterface.parseLog(log);
                
                if (parsedLog.name === 'GameOffered') {
                  const gameName = parsedLog.args.gameName;
                  baseTx.contractType = gameName; // Use actual game name
                  baseTx.action = 'Game Offered';
                  baseTx.gameId = parsedLog.args.offerId.toString();
                } else if (parsedLog.name === 'GameAccepted') {
                  baseTx.action = 'Game Accepted';
                  baseTx.gameId = parsedLog.args.gameID.toString();
                  const offerId = parsedLog.args.offerId.toString();
                  
                  // Try to look up the offer to get the game name
                  try {
                    const gameMakerContract = new ethers.Contract(
                      contractAddresses.GameMaker,
                      GameMakerABI.abi,
                      provider
                    );
                    const offer = await gameMakerContract.gameOffers(offerId);
                    baseTx.contractType = offer.gameName; // Use actual game name
                  } catch (lookupError) {
                    console.warn('Could not lookup offer details:', lookupError.message);
                    baseTx.contractType = 'GameAccepted'; // Fallback
                  }
                } else if (parsedLog.name === 'GameRetracted') {
                  baseTx.action = 'Game Retracted';
                  baseTx.gameId = parsedLog.args.offerId.toString();
                  baseTx.contractType = 'GameRetracted';
                }
              }
            } catch (e) {
              // Ignore parsing errors for non-GameMaker events
            }
          }
        }
        
        // Fallback if no specific event was found
        if (baseTx.contractType === 'Unknown') {
          baseTx.contractType = 'GameMaker';
          baseTx.action = 'Game Transaction';
        }
      } else if (contractAddress === contractAddresses.TicTacToe?.toLowerCase()) {
        baseTx.gameRelated = true;
        baseTx.contractType = 'TicTacToe';
        baseTx.action = 'Game Move';
      } else if (contractAddress === contractAddresses.RockPaperScissors?.toLowerCase()) {
        baseTx.gameRelated = true;
        baseTx.contractType = 'RockPaperScissors';
        baseTx.action = 'Game Action';
      } else if (contractAddress === contractAddresses.CoinFlip?.toLowerCase()) {
        baseTx.gameRelated = true;
        baseTx.contractType = 'CoinFlip';
        baseTx.action = 'Game Flip';
      } else if (contractAddress === contractAddresses.Risk?.toLowerCase() ||
                 contractAddress === contractAddresses.RiskClassic?.toLowerCase() ||
                 contractAddress === contractAddresses.RiskSimple?.toLowerCase()) {
        baseTx.gameRelated = true;
        baseTx.contractType = 'Risk';
        baseTx.action = 'Game Move';
      } else if (contractAddress === contractAddresses.Mill?.toLowerCase()) {
        baseTx.gameRelated = true;
        baseTx.contractType = 'Mill';
        baseTx.action = 'Game Move';
      }

      // Try to extract game ID from logs if not already found and not a GameMaker transaction
      if (baseTx.gameRelated && !baseTx.gameId && receipt && receipt.logs) {
        for (const log of receipt.logs) {
          try {
            // Most game events have gameId as first indexed parameter
            if (log.topics && log.topics.length > 1) {
              const potentialGameId = ethers.BigNumber.from(log.topics[1]);
              if (potentialGameId.lt(1000000)) { // Reasonable game ID range
                baseTx.gameId = potentialGameId.toString();
                break;
              }
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }

    } catch (error) {
      console.warn('Error analyzing transaction:', error.message);
    }

    return baseTx;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const canNavigateToGame = (tx) => {
    // Can navigate if we have a game ID and a valid game type
    if (!tx.gameId || !tx.contractType) return false;
    
    // List of game types that have game pages
    const validGameTypes = [
      'TicTacToe', 'RockPaperScissors', 'CoinFlip', 'Risk', 'Mill'
    ];
    
    return validGameTypes.includes(tx.contractType);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const handleTransferChange = (field, value) => {
    setTransferForm(prev => ({
      ...prev,
      [field]: value,
      transferError: '', // Clear errors when user types
      transferSuccess: false
    }));
  };

  const handleRecipientChange = (address) => {
    setTransferForm(prev => ({
      ...prev,
      recipient: address,
      transferError: '', // Clear errors when address changes
      transferSuccess: false
    }));
  };

  const validateTransfer = () => {
    const { recipient, amount } = transferForm;
    
    // Validate recipient address (AddressSelector already validates format)
    if (!recipient || recipient.trim() === '') {
      return 'Recipient address is required';
    }
    
    // Additional validation - ensure it's a different address from current wallet
    if (walletInfo?.address && recipient.toLowerCase() === walletInfo.address.toLowerCase()) {
      return 'Cannot send funds to yourself';
    }
    
    // Validate amount
    if (!amount || parseFloat(amount) <= 0) {
      return 'Amount must be greater than 0';
    }
    
    if (balance && parseFloat(amount) > parseFloat(balance)) {
      return 'Insufficient balance';
    }
    
    return null;
  };

  const handleWalletConnected = () => {
    setConnectionError('');
    // Reload wallet data now that a wallet is connected
    loadWalletData();
  };

  const handleWalletConnectionError = (error) => {
    setConnectionError(error);
  };

  const handleTransfer = async () => {
    const error = validateTransfer();
    if (error) {
      setTransferForm(prev => ({ ...prev, transferError: error }));
      return;
    }
    
    try {
      setTransferForm(prev => ({ ...prev, isTransferring: true, transferError: '', transferSuccess: false }));
      
      const signer = walletManager.getSigner();
      if (!signer) {
        throw new Error('No signer available');
      }
      
      const transaction = {
        to: transferForm.recipient,
        value: ethers.utils.parseEther(transferForm.amount)
      };
      
      console.log('Sending transaction:', transaction);
      const tx = await signer.sendTransaction(transaction);
      console.log('Transaction sent:', tx.hash);
      
      // Wait for confirmation
      await tx.wait();
      console.log('Transaction confirmed');
      
      // Update success state
      setTransferForm(prev => ({ 
        ...prev, 
        isTransferring: false, 
        transferSuccess: true,
        recipient: '',
        amount: ''
      }));
      
      // Refresh balance, DICY tokens, and transactions
      if (walletInfo?.address) {
        await Promise.all([
          fetchBalance(walletInfo.address),
          fetchDicyTokens(walletInfo.address),
          fetchAllTransactions(walletInfo.address)
        ]);
      }
      
    } catch (error) {
      console.error('Transfer failed:', error);
      setTransferForm(prev => ({ 
        ...prev, 
        isTransferring: false, 
        transferError: error.message || 'Transfer failed'
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading wallet details...</p>
        </div>
      </div>
    );
  }

  if (!walletInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to GameMaker
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Connect Your Wallet</h1>
            <p className="text-gray-600 mt-2">Choose your preferred wallet connection method to get started</p>
          </div>

          {/* Connection Error */}
          {connectionError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Connection Failed</h3>
                  <p className="text-sm text-red-700 mt-1">{connectionError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Selection */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <WalletSelection
              onWalletConnected={handleWalletConnected}
              onError={handleWalletConnectionError}
            />
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-800">Need Help?</h3>
                <div className="text-sm text-blue-700 mt-1">
                  <p className="mb-2">Choose from the following connection options:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>MetaMask:</strong> Connect your existing MetaMask browser extension</li>
                    <li><strong>Custom RPC Wallet:</strong> Use a private key with custom network settings</li>
                  </ul>
                  <p className="mt-2">Your wallet information will be displayed once connected successfully.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to GameMaker
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Wallet Details</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Wallet Info and Transfer */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Wallet Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="text-lg">{walletInfo.type === WALLET_TYPES.CUSTOM_RPC ? 'Custom RPC Wallet' : 'MetaMask'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <div className="font-mono text-sm bg-gray-100 p-3 rounded break-all flex items-center justify-between">
                    <AddressDisplayLong address={walletInfo.address} copyable={false} />
                    <button
                      onClick={() => copyToClipboard(walletInfo.address)}
                      className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                      title="Copy address"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Balance</label>
                  <p className="text-2xl font-mono text-green-600">
                    {balance !== null ? `${parseFloat(balance).toFixed(6)} POL` : 'Loading...'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">DICY Tokens</label>
                  <div className="space-y-1">
                    <p className="text-lg font-mono text-blue-600">
                      {dicyTokens !== null ? dicyTokens.tokens : 'Loading...'}
                    </p>
                    {dicyTokens && dicyTokens.value && parseFloat(dicyTokens.value) > 0 && (
                      <p className="text-sm text-gray-600">
                        Value: {parseFloat(dicyTokens.value).toFixed(6)} POL
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Transfer Funds Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Transfer Funds</h2>
              
              <div className="space-y-4">
                <div>
                  <AddressSelector
                    selectedAddress={transferForm.recipient}
                    onAddressChange={handleRecipientChange}
                    label="Recipient Address"
                    placeholder="0x... or select from address book"
                    className={transferForm.isTransferring ? 'pointer-events-none opacity-50' : ''}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount (POL)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={transferForm.amount}
                    onChange={(e) => handleTransferChange('amount', e.target.value)}
                    placeholder="0.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={transferForm.isTransferring}
                  />
                  {balance && (
                    <p className="text-sm text-gray-500 mt-1">
                      Available: {parseFloat(balance).toFixed(6)} POL
                    </p>
                  )}
                </div>
                
                {transferForm.transferError && (
                  <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                    {transferForm.transferError}
                  </div>
                )}
                
                {transferForm.transferSuccess && (
                  <div className="text-green-600 text-sm bg-green-50 p-3 rounded-md">
                    Transfer successful! Transaction has been confirmed.
                  </div>
                )}
                
                <button
                  onClick={handleTransfer}
                  disabled={transferForm.isTransferring || !transferForm.recipient || !transferForm.amount}
                  className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                    transferForm.isTransferring || !transferForm.recipient || !transferForm.amount
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {transferForm.isTransferring ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Transfer'
                  )}
                </button>
                
                <div className="text-xs text-gray-500">
                  <p>• Double-check the recipient address before sending</p>
                  <p>• Transactions cannot be reversed</p>
                  <p>• Gas fees will be deducted from your balance</p>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction History */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Transaction History</h2>
                {analysisLoading && (
                  <div className="flex items-center text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Analyzing...
                  </div>
                )}
              </div>

              {transactions.length === 0 ? (
                <p className="text-gray-600">No transactions found</p>
              ) : (
                <div className="space-y-4">
                  {transactions.map((tx, index) => (
                    <div key={tx.hash || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className={`inline-block w-3 h-3 rounded-full mr-3 ${
                              tx.isIncoming ? 'bg-green-400' : 'bg-red-400'
                            }`}></span>
                            <span className="font-mono text-sm text-gray-600">
                              {tx.hash ? `${tx.hash.slice(0, 16)}...` : 'Unknown'}
                            </span>
                            <button
                              onClick={() => copyToClipboard(tx.hash)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                              title="Copy transaction hash"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Amount:</span>
                              <span className={`ml-2 font-mono ${tx.isIncoming ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.isIncoming ? '+' : '-'}{parseFloat(tx.value).toFixed(6)} POL
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Block:</span>
                              <span className="ml-2 font-mono">#{tx.blockNumber}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <span className={`ml-2 ${tx.status === 'Success' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.status || 'Unknown'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Gas Used:</span>
                              <span className="ml-2 font-mono">{tx.gasUsed}</span>
                            </div>
                          </div>

                          {tx.gameRelated && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                              <div className="flex items-center mb-2">
                                <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                <span className="font-medium text-blue-800">GameMaker Transaction</span>
                              </div>
                              <div className="text-sm text-blue-700">
                                <p><span className="font-medium">Game:</span> {tx.contractType}</p>
                                <p><span className="font-medium">Action:</span> {tx.action}</p>
                                {tx.gameId && (
                                  <p><span className="font-medium">Game ID:</span> #{tx.gameId}
                                    {canNavigateToGame(tx) && (
                                      <button
                                        onClick={() => navigate(`/${tx.contractType.toLowerCase()}/${tx.gameId}`)}
                                        className="ml-2 text-blue-600 hover:text-blue-800 underline"
                                      >
                                        View Game
                                      </button>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletDetails;